<template>
  <div class="samples-cards">
    <div
      v-for="(sample, index) in samples"
      :key="index"
    >
      <sample-compact-card
        v-if="showCompactView"
        :sample="sample.samSamplenumber"
        :disabled="disableCard(sample)"
        :is-active="cardActive === sample.samId || sample.samId === sampleId"
        :is-child="false"
        @cardSelected="cardSelected(sample)"
      />

      <div
        v-if="!showCompactView"
        qa="samples-cards-div"
        class="samplesCards component-inside-1--hover mt-2"
        :class="{'card-active': cardActive === sample.samId || sample.samId === sampleId,
                 'disabled': disableCard(sample), 'depleted-card': sample.isDepleted}"
        @click="disableCard(sample) ? () => {} : cardSelected(sample)"
      >
        <div class="py-1 px-2">
          <b-row>
            <b-col class="pt-1 pl-4 pr-2 pb-0 d-flex justify-content-between">
              <b-row class="w-100 d-flex justify-content-between">
                <b-col cols="auto">
                  <div class="d-flex align-items-center">
                    <span
                      class="sample-number primary-dark-color"
                      qa="samples-cards-label-span"
                    >
                      {{ sample.samSamplenumber }}
                    </span>
                    <vtBubble
                      v-if="sample.incidentsCount"
                      :number="sample.incidentsCount"
                      :type="true"
                      :tooltip="' '+$tc('grid.detail.hasAlarms')"
                      @click="$emit('showIncidentsModal', sample.samId)"
                    />
                    <vtBubble
                      v-if="sample.attachmentsCount"
                      :number="sample.attachmentsCount"
                      :type="false"
                      :tooltip="' '+$tc('global.file',2)"
                      @click="$emit('show-sample-attachment-tree-modal', sample.samId)"
                    />
                  </div>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
          <b-row>
            <b-col
              cols="11"
              class="pt-0 pl-4 pr-2"
            >
              <p
                class="mt-1 secondary-color"
                qa="samples-cards-code-p"
              >
                {{ sample.samTopographicCodeDescription }}
              </p>
              <p
                class="mt-1 sample-description tertiary-color"
                qa="samples-cards-type-description-span"
              >
                {{ sample.sampleType.description }}
              </p>
              <div class="d-flex pb-0 mt-2">
                <common-sample-status-badge :status="sample.status" />
              </div>
              <div v-if="sample.isDepleted">
                <span class="font-size--small-1"><i
                  class="far fa-empty-set mr-1"
                  aria-hidden="true"
                />{{ $t('global.depletedSample') }}</span>
              </div>
              <div class="mt-2 d-flex align-items-center">
                <i
                  v-for="tag in sample.tags"
                  :key="tag.id"
                  v-tooltip="tag.name"
                  class="mr-1 fas fa-square"
                  :style="'color: ' + tag.color"
                  aria-hidden="true"
                />
                <div class="cardBtns">
                  <i
                    v-if="sample.process"
                    v-tooltip="sample.process.name"
                    aria-hidden="true"
                    class="fas fa-tint alt-color"
                  />
                  <i
                    v-if="sample.samSpecialPreserve"
                    v-tooltip="$t('grossing.labels.preserve') + ': ' + (sample.samDaysBeforeDisposal ? (sample.samDaysBeforeDisposal + ' ' + $t('grossing.labels.days')) : $t('grossing.content.presOptionIndefinite'))"
                    aria-hidden="true"
                    class="fas fa-clock alt-color"
                  />
                  <i
                    v-if="sample.samFixerFxrId"
                    v-tooltip="sample.samFixerFxrCode"
                    aria-hidden="true"
                    class="fas fa-certificate alt-color"
                  />
                  <i
                    v-if="sample && sample.currentLaboratory && sample.currentLaboratory.labId !== laboratoryId"
                    v-tooltip="`${$t('global.elementLocatedIn')} ${sample.currentLaboratory.labName}`"
                    aria-hidden="true"
                    class="fas fa-map-marker-alt alt-color"
                  />
                  <b-button
                    v-if="showAnalysisResultButton(sample)"
                    v-tooltip="analysisStatusName(sample)"
                    :style="{ color: analysisStatusColor(sample) }"
                    qa="sample-card-ia-button"
                    class="btn__icon py-0 btn__icon--ai-failure"
                  >
                    <span class="fas fa-brain" />
                  </b-button>
                  <i
                    v-if="visibleHasUnauthorizedBillableConceptsIcon(sample.hasUnauthorizedBillableConcepts)"
                    v-tooltip="$t('global.hasUnauthorizedBillableConcepts')"
                    aria-hidden="true"
                    class="fas fa-calculator danger-color"
                  />
                  <template v-if="sample.revisions.length">
                    <button
                      id="pop-review"
                      type="button"
                      :class="[allEntityRevisionsCompleted(sample.revisions) ? 'completed' : 'pending']"
                      class="btn btn-round p-0"
                      @click="onShowRevisionPopoverClick(sample, ENTITY_TYPE_SAMPLE, sample.revisions)"
                    >
                      <i
                        v-tooltip="{ title: allEntityRevisionsCompleted(sample.revisions) ? $tc('diagnosis.labels.reviewCompleted', 1) : $t('diagnosis.labels.pendingReview') }"
                        aria-hidden="true"
                        class="far fa-tasks"
                      />
                    </button>
                    <b-popover
                      v-if="sample.revisions.length > 1"
                      :show.sync="showRevisionsPopover"
                      target="pop-review"
                      placement="top"
                      triggers="click blur"
                    >
                      <div class="review-pop">
                        <ul>
                          <li
                            v-if="sample.revisions.find(revision => !revision.finished)"
                            @click="mapAndShowRevisionInfo(sample, ENTITY_TYPE_SAMPLE, sample.revisions[0])"
                          >
                            {{ $t('diagnosis.labels.reviewInformation') }}
                          </li>
                          <template v-if="sample.revisions.filter(revision => revision.finished).length">
                            <hr class="my-0">
                            <div class="p-1 mx-3">
                              <span class="main">{{ $tc('diagnosis.labels.reviewCompleted', 2) }}</span>
                            </div>
                            <li
                              v-for="(revision, indexRevision) in sample.revisions.filter(revision => revision.finished)"
                              :key="revision.id"
                              @click="mapAndShowRevisionInfo(sample, ENTITY_TYPE_SAMPLE, revision)"
                            >
                              {{ $t('diagnosis.labels.review') }} {{ indexRevision + 1 }}
                            </li>
                          </template>
                        </ul>
                      </div>
                    </b-popover>
                  </template>
                </div>
              </div>
            </b-col>
            <div
              v-if="sample.subSamples.size > 0"
              class="arrowSub"
            >
              <b-button
                class="btn__icon btn__icon--primary pt-1 pr-0 pb-0"
                @click="showSubcards = !showSubcards"
              >
                <i
                  v-show="showSubcards"
                  class="fal fa-chevron-up"
                  aria-hidden="true"
                />
                <i
                  v-show="!showSubcards"
                  class="fal fa-chevron-down"
                  aria-hidden="true"
                />
              </b-button>
            </div>
          </b-row>
        </div>
      </div>
      <div
        v-if="showSubcards"
        :class="showCompactView ? 'subSampleCardCompact' : 'subSampleCard'"
      >
        <div
          v-if="!showCompactView"
          class="subcardLine"
        />
        <div
          v-for="subSample in sample.subSamples"
          :key="subSample.samId"
        >
          <sub-sample-card
            :sample="subSample"
            :is-active="cardActive === subSample.samId || subSample.samId === sampleId"
            :show-compact-view="showCompactView"
            @showIncidentsModal="$emit('showIncidentsModal', $event)"
            @cardSelected="cardSelected(subSample)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VtBubble from '@/components/common/VtBubble'
import SubSampleCard from '@/components/samplesCards/SubSampleCard'
import commonSampleService, { SAMPLE_TYPE_WORKFLOW } from '@/api/resources/common/commonSampleService'
import SampleCompactCard from '@/components/samplesCards/SampleCompactCard'
import CommonSampleStatusBadge from '@/components/common/CommonSampleStatusBadge'
import { SAMPLE_STATES } from '@/api/resources/entityState/sample-state'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/resources/attachment/attachmentResource'
import { ModuleOperationGuard } from '@/api/operations/guards/module-operation-guard'

export default {
    name: 'SamplesCards',
    components: {
        VtBubble,
        SubSampleCard,
        SampleCompactCard,
        CommonSampleStatusBadge
    },
    props: {
        canDisableCards: { type: Boolean, default: false },
        showCompactView: { type: Boolean, default: false },
        laboratoryId: { type: Number },
        caseId: { type: Number },
        sampleId: { type: Number },
        routeIsGrossing: { type: Boolean },
        routeIsLaboratory: { type: Boolean }
    },
    data() {
        return {
            SAMPLE_TYPE_WORKFLOW: SAMPLE_TYPE_WORKFLOW,
            showSubcards: true,
            cardActive: null,
            samples: [],
            showRevisionsPopover: false
        }
    },
    computed: {
        ENTITY_TYPE_SAMPLE() {
            return ATTACHMENT_ENTITY_TYPE.SAMPLE
        },
        isAnySamplePendingGrossing() {
            return this.samples &&
                this.samples.some(sample =>
                    [SAMPLE_STATES.REGISTERED.code, SAMPLE_STATES.REPROCESSING_REQUESTED.code].includes(sample.status.code))
        }
    },
    methods: {
        analysisStatusName(sample) {
            return sample.analysisStatusAI?.name ? this.$tc(sample.analysisStatusAI.name) : null
        },
        analysisStatusColor(sample) {
            return sample.analysisStatusAI?.color
        },
        showAnalysisResultButton(sample) {
            return sample.analysisStatusAI && this.$route.name === 'diagnosisCaseInform'
        },
        async loadSamples() {
            const [err, response] = await this.toCall(commonSampleService.getSamplesOfCaseAsCards(this.laboratoryId, this.caseId))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.samples = response
        },
        mapAndShowRevisionInfo(entity, entityType, revision) {
            const reviewInfoData = {
                revision: revision,
                entitySelected: entity,
                entityType: entityType
            }
            this.$emit('showReviewInfoModal', reviewInfoData)
        },
        onShowRevisionPopoverClick(entity, entityType) {
            if (entity.revisions.length === 1) {
                this.mapAndShowRevisionInfo(entity, entityType, entity.revisions[0])
            }
        },
        allEntityRevisionsCompleted(revisions) {
            return revisions.filter(revision => revision.finished).length === revisions.length
        },
        cardSelected(sample) {
            this.$emit('cardSelected', sample)
        },
        disableCard(sample) {
            if (this.canDisableCards) {
                switch (sample.sampleType.workflow) {
                case SAMPLE_TYPE_WORKFLOW.PROCESSING_CYTOLOGIES.code:
                    return !this.routeIsLaboratory
                case SAMPLE_TYPE_WORKFLOW.GROSSING_BIOPSIES.code:
                    return !this.routeIsGrossing
                }
            }
            return false
        },
        visibleHasUnauthorizedBillableConceptsIcon(sampleHasUnauthorizedBillableConcepts) {
            return ModuleOperationGuard.canAccessBilling && sampleHasUnauthorizedBillableConcepts
        }
    }
}

</script>

<style lang="scss">

  .samples-cards .btn-round.pending {
      color: vt-color(orange-medium);
  }
  .samples-cards .btn-round.completed {
      color: vt-color(green-radiant);
  }
  .review-pop {
      padding: 0 !important;
  }
  .review-pop ul {
      padding: 0;
      margin: 5px 0;
  }
  .review-pop ul li {
      list-style: none;
      padding: 2px 20px;
      font-size: 12px;
      cursor: pointer;
  }
  .review-pop ul li:hover {
      background: vt-color(light-primary);
  }
  .review-pop .pop-header {
      background: vt-color(base-text, .9);
      padding: 8px 12px;
      font-weight: 700;
  }
  .label-teaching {
    color: #00a99d;
  }
  .label-bank {
    color: #29abe2;
  }
  .label-different {
    color: #fbb03b;
  }
  .label-until {
    color: #d4145a;
  }
  .samplesCards .badge{
    font-weight: normal;
    font-size: font-size(very-tiny) !important;
  }
</style>

<style lang="scss" scoped>

  .samplesCards {
    z-index: 1;
    position: relative;
      .sample-number {
          font-weight: bold;
          font-size: font-size(normal);
      }
      .sample-description {
          font-size: font-size(very-tiny);
      }
  }

  .samplesCards .card-body {
    background: vt-color(subcard-bg);
  }

  .samplesCards.depleted-card .card-body {
      background: vt-color(depleted-card);
  }

  .samplesCards .sample-line-height span{
    line-height: 14px;
    margin: 1px 0 5px 0;
  }

  .samplesCards hr {
    border-color: vt-color(card-border);
  }

  .card-active {
    box-shadow: 0 3px 10px -1px vt-color(primary, .5);
      &:before {
          content: '';
          width: 4px;
          height: 100%;
          position: absolute;
          background: vt-color(primary);
          left: 0;
      }
  }

  .disabled {
      opacity: .5;
      cursor: default;
  }

  .samplesCards h4 {
    font-weight: normal;
    color: vt-color(negative-text);
  }

  .samplesCards p {
    font-size: font-size(small);
    margin-bottom: 0;
    line-height: 12px;
  }

  .samplesCards .arrowSub {
    position: absolute;
    right: 5px;
    bottom: 0;
  }

  .samplesCards .cardBtns i {
    padding: 2px;
  }

  .label-teaching {
    color: #00a99d;
  }

  .label-bank {
    color: #29abe2;
  }

  .label-different {
    color: #fbb03b;
  }

  .label-until {
    color: #d4145a;
  }

  /*-- Grossing Subcards styles--*/

  .subSampleCard {
    padding-left: 8%;
    margin-bottom: 10%;
    position: relative;
  }

  .subSampleCardCompact {
    position: relative;
  }

  .subcardLine {
    background: vt-color(primary);
    width: 8px;
    height: 100%;
    position: absolute;
    left: 12%;
    top: -10px;
  }

</style>
